/* eslint-disable import/prefer-default-export */
import queryString from 'query-string';
import { generatePath } from 'react-router-dom';

import {
  API_AD_GROUP_NEW_URL,
  API_AD_GROUP_PAUSE_TOGGLE_URL,
  API_AD_GROUP_STATE_STRING_URL,
  API_AD_GROUP_TARGETING_DEFAULTS_URL,
  API_AD_GROUP_TILE_ESTIMATE_URL,
  API_AD_GROUP_URL,
  API_AD_GROUPS_URL,
  API_COPY_AD_GROUP_URL,
  API_PASTE_AD_GROUP_URL,
  API_VERIONS_AD_GROUPS_URL,
  API_VERIONS_META_AD_GROUPS_URL,
} from '../../constants/apiUrls';
import { PageableList } from '../../types/contract';
import { getRequestJSON, postRequestJSON, putRequestJSON } from '../../util/services';

interface QueryParams {
  page?: number;
  perPage?: number;
}

export const getAll = (
  apiHost: string,
  lineItemId: number,
  { page, perPage }: QueryParams
): Promise<PageableList<any>> =>
  getRequestJSON(
    apiHost,
    `${generatePath(API_AD_GROUPS_URL, { lineItemId: lineItemId.toString() })}?${queryString.stringify({
      page: page + 1,
      per_page: perPage,
    })}`
  );

export const getNew = (apiHost: string, lineItemId: number): Promise<any> =>
  getRequestJSON(apiHost, generatePath(API_AD_GROUP_NEW_URL, { lineItemId: lineItemId.toString() }));

export const getTargetingDefaults = (apiHost: string, lineItemId: number, adGroup: any): Promise<any> =>
  postRequestJSON(
    apiHost,
    generatePath(API_AD_GROUP_TARGETING_DEFAULTS_URL, { lineItemId: lineItemId.toString() }),
    adGroup
  );

export const get = (apiHost: string, adGroupId: number): Promise<any> =>
  getRequestJSON(apiHost, generatePath(API_AD_GROUP_URL, { adGroupId: adGroupId.toString() }));

export const saveAdGroup = (apiHost: string, adGroup: any): Promise<any> => {
  // TODO: update ad group mapper to return object with camelCase keys
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { id, insertion_order_id } = adGroup;
  if (id) {
    return putRequestJSON(apiHost, generatePath(API_AD_GROUP_URL, { adGroupId: id }), adGroup);
  }
  return postRequestJSON(apiHost, generatePath(API_AD_GROUPS_URL, { lineItemId: insertion_order_id }), adGroup);
};

export const getVersions = (
  apiHost: string,
  adGroupId: number,
  page: number,
  perPage: number,
  modelType: string = 'Campaign',
  filters: Object = {}
): Promise<any> =>
  new Promise((resolve, reject) => {
    const queryParams = `?${queryString.stringify({
      ...filters,
      model_type: modelType.toString(),
      page: page + 1,
      per_page: perPage.toString(),
    })}`;
    const getVersionsAsyncHelper = async () => {
      const versions = await getRequestJSON(
        apiHost,
        generatePath(API_VERIONS_AD_GROUPS_URL, {
          adGroupId: adGroupId.toString(),
          '*': queryParams,
        })
      );
      const meta = await getRequestJSON(
        apiHost,
        generatePath(API_VERIONS_META_AD_GROUPS_URL, {
          adGroupId: adGroupId.toString(),
          '*': `?${queryString.stringify({
            model_type: modelType.toString(),
          })}`,
        })
      );

      if (versions && meta) {
        resolve({
          ...versions,
          meta,
        });
      } else {
        reject();
      }
    };

    getVersionsAsyncHelper();
  });

export const copy = (apiHost: string, adGroupId: number): Promise<any> =>
  getRequestJSON(apiHost, generatePath(API_COPY_AD_GROUP_URL, { adGroupId: adGroupId.toString() }));

export const paste = (apiHost: string, adGroupId: number, payload): Promise<any> =>
  postRequestJSON(apiHost, generatePath(API_PASTE_AD_GROUP_URL, { adGroupId: adGroupId.toString() }), payload);

export const getStateString = (apiHost: string, adGroupId: number): Promise<any> =>
  getRequestJSON(apiHost, generatePath(API_AD_GROUP_STATE_STRING_URL, { adGroupId: adGroupId.toString() }));

export const getTileEstimate = (apiHost: string, adGroupId: number): Promise<any> =>
  getRequestJSON(apiHost, generatePath(API_AD_GROUP_TILE_ESTIMATE_URL, { adGroupId: adGroupId.toString() }));

export const pauseToggle = (apiHost: string, adGroupId: number): Promise<any> =>
  postRequestJSON(apiHost, generatePath(API_AD_GROUP_PAUSE_TOGGLE_URL, { adGroupId: adGroupId.toString() }), undefined);
